import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_PROD_URL } from "src/utils/axios";
import jsCookie from "js-cookie";
import toast from "react-hot-toast";
import { createColumnHelper } from "@tanstack/react-table";
import Loading from "src/components/reusable/loading";
import ReactTable from "src/components/reusable/ReactTable";
import { ChevronLeft, ChevronRight } from "lucide-react";
interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
}

const ContactsWidget = () => {
  const { id } = useParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [emailHtml, setEmailHtml] = useState<string | null>(null);
  const [emailSubject, setEmailSubject] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 5;

  const totalPages = Math.ceil(contacts.length / contactsPerPage);
  const startIdx = (currentPage - 1) * contactsPerPage;
  const endIdx = startIdx + contactsPerPage;
  const currentContacts = contacts.slice(startIdx, endIdx);

  // ✅ Moved outside useEffect so we can reuse it
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_PROD_URL}/contacts`,
        {},
        {
          params: {
            userId: jsCookie.get("user_id"),
            threadId: id,
          },
        }
      );
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error("Failed to fetch contacts", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [id]);

  // --- Selection ---
  const handleSelect = (contactId: number) => {
    setSelected((prev) =>
      prev.includes(contactId)
        ? prev.filter((i) => i !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      const visibleIds = currentContacts.map((c) => c.id);
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      const visibleIds = contacts.map((c) => c.id);
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    const visibleIds = currentContacts.map((c) => c.id);
    const allVisibleSelected = visibleIds.every((id) => selected.includes(id));
    setSelectAll(allVisibleSelected);
  }, [currentPage, selected, contacts]);

  // --- Email Functions ---
  const handlePreviewEmail = async () => {
    if (selected.length === 0) return;

    const selectedEmails = contacts
      .filter((c) => selected.includes(c.id))
      .map((c) => c.email);
    localStorage.setItem("selectedContactEmails", JSON.stringify(selectedEmails));

    setPreviewOpen(true);
    setEmailLoading(true);
    setEmailHtml(null);
    setEmailSubject(null);

    try {
      const response = await axios.get(
        `${API_PROD_URL}/marketing-agent/campaign/email_template`,
        { params: { campaign_id: id } }
      );
      setEmailHtml(response.data.html || "<p>No content available.</p>");
      setEmailSubject(response.data.subject || "(No Subject)");
    } catch (err) {
      console.error("Failed to fetch email template", err);
      setEmailHtml("<p style='color:red;'>Failed to load email preview.</p>");
      setEmailSubject("(Error loading subject)");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendEmail = async () => {
    const selectedEmails = JSON.parse(
      localStorage.getItem("selectedContactEmails") || "[]"
    );
    if (!selectedEmails.length || !emailSubject || !emailHtml) {
      console.error("Missing required data for sending email");
      return;
    }

    try {
      await axios.post(
        `${API_PROD_URL}/send-bulk-email`,
        {
          to_emails: selectedEmails,
          subject: emailSubject,
          body: emailHtml,
        },
        {
          params: {
            userId: jsCookie.get("user_id"),
            campaignId: id,
          },
        }
      );
      toast.success("Emails sent successfully!");
    } catch (err) {
      console.error("Failed to send email:", err);
      toast.error("Failed to send emails");
    }
  };

  const handleEmailRegenerate = async () => {
    setEmailLoading(true);
    setEmailHtml(null);
    setEmailSubject(null);

    try {
      const response = await axios.get(
        `${API_PROD_URL}/marketing-agent/campaign/email_template`,
        { params: { campaign_id: id, regenerate: true } }
      );
      setEmailHtml(response.data.html || "<p>No content available.</p>");
      setEmailSubject(response.data.subject || "(No Subject)");
    } catch (err) {
      console.error("Failed to fetch email template", err);
      setEmailHtml("<p style='color:red;'>Failed to load email preview.</p>");
      setEmailSubject("(Error loading subject)");
    } finally {
      setEmailLoading(false);
    }
  };

  // --- Table ---
  const columnHelper = createColumnHelper<Contact>();
  const columns = [
    {
      id: "select",
      header: () => (
        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
      ),
      cell: (info: any) => (
        <input
          type="checkbox"
          checked={selected.includes(info.row.original.id)}
          onChange={() => handleSelect(info.row.original.id)}
        />
      ),
      size: 50,
    },
    { header: "First Name", accessorKey: "first_name" },
    { header: "Last Name", accessorKey: "last_name" },
    { header: "Email", accessorKey: "email" },
    {
      header: "Created At",
      accessorKey: "created_at",
      cell: (info: any) => {
        const val = info.getValue();
        return val ? new Date(String(val)).toLocaleDateString() : "N/A";
      },
    },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm p-3 border-2 border-gray-200 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium">Contacts</h2>
        <CSVImportModal onContactsCreated={fetchContacts} /> {/* ✅ updated */}
      </div>

      {loading ? (
        <LoadingIcon fontSize={30} className="animate-spin w-full flex justify-center items-center text-primary-900" />
      ) : contacts.length === 0 ? (
        <p className="text-gray-500 text-center">No contacts available</p>
      ) : (
        <>
          <ReactTable columnsData={columns} rowsData={currentContacts} />

          {/* Pagination */}
          <div className="flex items-center justify-end mt-4 space-x-2">
            <button
              className={`${currentPage === 1
                ? " text-gray-500 cursor-not-allowed"
                : " text-gray-900"
                }`}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="text-sm text-gray-700">{currentPage}</span>

            <button
              className={`${currentPage === totalPages
                ? " text-gray-500 cursor-not-allowed"
                : "text-gray-900"
                }`}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="flex space-x-2 mt-3">
            <button
              className={`px-3 py-1 rounded-md ${selected.length === 0
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-[#442873] text-white hover:bg-[#442873]/90"
                }`}
              onClick={handlePreviewEmail}
              disabled={selected.length === 0}
            >
              Preview Email
            </button>
          </div>

          {/* Modal */}
          {previewOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white w-11/12 max-w-3xl rounded-lg shadow-lg overflow-auto max-h-[80vh]">
                <div className="flex justify-between items-center p-3 border-b">
                  <h3 className="font-semibold text-lg">
                    {emailSubject || "Loading..."}
                  </h3>
                  <button
                    className="text-gray-600 hover:text-gray-900"
                    onClick={() => setPreviewOpen(false)}
                  >
                    ✕
                  </button>
                </div>

                <div className="p-4 overflow-auto">
                  {emailLoading ? (
                    <div className="flex justify-center py-8 text-gray-500">
                      <Loading width="23px" height="23px" isLoading={true} />
                    </div>
                  ) : (
                    <div
                      className="prose max-w-full"
                      dangerouslySetInnerHTML={{ __html: emailHtml || "" }}
                    />
                  )}
                </div>

                <div className="flex justify-end p-3 border-t space-x-2">
                  {!emailLoading && emailHtml && (
                    <button
                      onClick={handleEmailRegenerate}
                      className="px-3 py-1 bg-[#442873] text-white rounded-md hover:bg-[#442873]/90"
                    >
                      Regenerate
                    </button>
                  )}

                  {!emailLoading && (
                    <button
                      className="px-3 py-1 bg-[#442873] text-white rounded-md hover:bg-[#442873]/90"
                      onClick={async () => {
                        await handleSendEmail();
                        setPreviewOpen(false);
                      }}
                    >
                      Send
                    </button>
                  )}
                  <button
                    className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    onClick={() => setPreviewOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactsWidget;


import Papa from "papaparse";
import { LoadingIcon } from "src/components/icons";


const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "middlename",
  "email",
  "contact_number",
  "description",
];

/* eslint-disable react/prop-types */
interface CSVImportModalProps {
  onContactsCreated?: () => void;
}

export const CSVImportModal: React.FC<CSVImportModalProps> = ({ onContactsCreated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "text/csv") {
      alert("Please upload a valid CSV file.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: { data: any[] }) => {
        if (results.data.length > 0) {
          setCsvHeaders(Object.keys(results.data[0]));
          setCsvData(results.data);
          setMappings({});
        } else {
          alert("No data found in CSV.");
        }
      },
    });
  };

  const handleMappingChange = (targetField: string, csvField: string) => {
    setMappings((prev) => ({ ...prev, [targetField]: csvField }));
  };

  const handleCreateContacts = async () => {
    const allMapped = REQUIRED_FIELDS.every((f) => mappings[f]);
    if (!allMapped) {
      alert("Please map all required fields before creating contacts.");
      return;
    }

    const mapped = csvData.map((row) => {
      const obj: Record<string, string> = {};
      REQUIRED_FIELDS.forEach((field) => {
        const csvField = mappings[field];
        obj[field] = csvField ? row[csvField] || "" : "";
      });
      return obj;
    });

    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const thread_id = parts[parts.length - 1];
      const user_id = jsCookie.get("user_id");

      await axios.post(`${API_PROD_URL}/contacts/bulk`, mapped, {
        params: { user_id, thread_id },
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Contacts created successfully");
      if (onContactsCreated) onContactsCreated(); // ✅ notify parent
      closeModal();
    } catch (err) {
      console.error("Failed to create contacts", err);
      toast.error("Failed to create contacts");
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        className="px-4 py-1 bg-[#442873] text-white rounded-md hover:bg-[#442873]/90"
      >
        Import CSV
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-xl relative">
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold mb-4">Import Contacts</h2>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="border border-gray-300 rounded-md p-2 w-full cursor-pointer mb-4"
            />

            {csvHeaders.length > 0 && (
              <>
                <h3 className="font-medium mb-2 text-sm">Map CSV Columns:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {REQUIRED_FIELDS.map((field) => (
                    <div key={field} className="flex flex-col">
                      <label className="text-xs text-gray-600 mb-1">{field}</label>
                      <select
                        className="border border-gray-300 rounded-md p-1 text-sm"
                        value={mappings[field] || ""}
                        onChange={(e) => handleMappingChange(field, e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {csvHeaders.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <button
                  className="mt-4 px-4 py-2 bg-[#442873] text-white rounded-md hover:bg-[#442873]/90"
                  onClick={handleCreateContacts}
                >
                  Create Contacts
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
